// ===========================================
// Change Password Dialog
// ===========================================

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  TextField,
  Typography,
} from '@mui/material';
import {
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useState } from 'react';
import { UseMutationResult } from '@tanstack/react-query';
import { calculatePasswordStrength } from './account.helpers';
import type { PasswordFormData } from './account.types';

interface Props {
  open: boolean;
  onClose: () => void;
  changePasswordMutation: UseMutationResult<unknown, unknown, { currentPassword: string; newPassword: string }>;
  onSuccess: () => void;
  onError: (message: string) => void;
}

export default function ChangePasswordDialog({
  open,
  onClose,
  changePasswordMutation,
  onSuccess,
  onError,
}: Props) {
  const [passwordForm, setPasswordForm] = useState<PasswordFormData>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const handleClose = () => {
    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    changePasswordMutation.reset();
    onClose();
  };

  const handleChangePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      onError('New passwords do not match');
      return;
    }

    try {
      await changePasswordMutation.mutateAsync({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      handleClose();
      onSuccess();
    } catch {
      // Error handled by mutation
    }
  };

  const strength = passwordForm.newPassword
    ? calculatePasswordStrength(passwordForm.newPassword)
    : null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Change Password</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <TextField
            fullWidth
            label="Current Password"
            type={showCurrentPassword ? 'text' : 'password'}
            value={passwordForm.currentPassword}
            onChange={(e) =>
              setPasswordForm({ ...passwordForm, currentPassword: e.target.value })
            }
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    edge="end"
                  >
                    {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            label="New Password"
            type={showNewPassword ? 'text' : 'password'}
            value={passwordForm.newPassword}
            onChange={(e) =>
              setPasswordForm({ ...passwordForm, newPassword: e.target.value })
            }
            sx={{ mb: 1 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    edge="end"
                  >
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {/* Password Strength Indicator */}
          {strength && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <LinearProgress
                  variant="determinate"
                  value={strength.score}
                  color={strength.color}
                  sx={{ flex: 1, height: 6, borderRadius: 3 }}
                />
                <Typography
                  variant="caption"
                  color={`${strength.color}.main`}
                  sx={{ minWidth: 50 }}
                >
                  {strength.label}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {strength.requirements.map((req, idx) => (
                  <Chip
                    key={idx}
                    label={req.label}
                    size="small"
                    color={req.met ? 'success' : 'default'}
                    variant={req.met ? 'filled' : 'outlined'}
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                ))}
              </Box>
            </Box>
          )}
          {!passwordForm.newPassword && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 2 }}
            >
              At least 8 characters with uppercase, lowercase, number, and special character
            </Typography>
          )}
          <TextField
            fullWidth
            label="Confirm New Password"
            type="password"
            value={passwordForm.confirmPassword}
            onChange={(e) =>
              setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })
            }
            error={
              passwordForm.confirmPassword !== '' &&
              passwordForm.newPassword !== passwordForm.confirmPassword
            }
            helperText={
              passwordForm.confirmPassword !== '' &&
              passwordForm.newPassword !== passwordForm.confirmPassword
                ? 'Passwords do not match'
                : ''
            }
          />
          {changePasswordMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {(() => {
                const err = changePasswordMutation.error as {
                  response?: { data?: { message?: string } };
                  message?: string;
                };
                return (
                  err?.response?.data?.message ||
                  err?.message ||
                  'Failed to change password. Check your current password.'
                );
              })()}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleChangePassword}
          disabled={
            changePasswordMutation.isPending ||
            !passwordForm.currentPassword ||
            !passwordForm.newPassword ||
            passwordForm.newPassword !== passwordForm.confirmPassword
          }
          startIcon={
            changePasswordMutation.isPending ? <CircularProgress size={16} /> : <LockIcon />
          }
        >
          {changePasswordMutation.isPending ? 'Changing...' : 'Change Password'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
